import { useEffect, useRef } from 'react';
import { Icon } from './Icon';
import { cx } from './ui';
import { confettiBurst, haptic } from '../state/celebrate';

/**
 * The satisfying checkbox. Purely visual — the surrounding row owns the click — but it
 * celebrates the moment `checked` flips on: a spring pop, the tick drawing itself in, a
 * little burst of confetti in the person's colour, and a gentle buzz.
 *
 * Effects fire only on a false→true transition (never on first mount), so opening a list
 * full of already-done items doesn't set off fireworks.
 */
export function CheckToggle({
  checked,
  size = 24,
  color = 'var(--color-accent)',
  onColor = 'accent',
}: {
  checked: boolean;
  size?: number;
  /** Confetti colour — usually the person's colour. */
  color?: string;
  /** Which token fills the box when checked. */
  onColor?: 'accent' | 'success';
}) {
  const box = useRef<HTMLSpanElement>(null);
  const prev = useRef(checked);

  useEffect(() => {
    if (checked && !prev.current) {
      const el = box.current;
      if (el) {
        const r = el.getBoundingClientRect();
        // A CSS var resolves to text, not a colour value, so hand confetti a real hex.
        const c = color.startsWith('var(') ? undefined : color;
        confettiBurst(r.left + r.width / 2, r.top + r.height / 2, c);
        el.classList.remove('check-pop');
        // Reflow so the animation restarts even on rapid re-checks.
        void el.offsetWidth;
        el.classList.add('check-pop');
      }
      haptic();
    }
    prev.current = checked;
  }, [checked, color]);

  const fill = onColor === 'success' ? 'border-success bg-success text-[color:var(--color-bg)]' : 'border-accent bg-accent text-[color:var(--color-on-accent)]';

  return (
    <span
      ref={box}
      className={cx(
        'flex shrink-0 items-center justify-center rounded-[5px] border transition-colors duration-150',
        checked ? fill : 'border-border',
      )}
      style={{ width: size, height: size }}
    >
      {checked && <Icon name="check" size={Math.round(size * 0.5)} strokeWidth={2.2} className="check-draw" />}
    </span>
  );
}
